import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Download, Pencil, Printer, Trash2 } from 'lucide-react';
import Papa from 'papaparse';
import { useEffect, useState, type ReactNode } from 'react';
import { AmountInput } from '@/components/amount-input';
import { Combobox } from '@/components/combobox';
import { Checkbox, ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EmptyState, PageHeader } from '@/components/page-header';
import { serverFieldErrors } from '@/components/settings-section';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAccounts } from '@/lib/accounts';
import { api } from '@/lib/api';
import { formatTrn, useMe } from '@/lib/clients';
import { useContacts } from '@/lib/contacts';
import { displayDate } from '@/lib/dates';
import { formatAmount } from '@/lib/format';
import { useOrgId } from '@/lib/org';
import { downloadCsv } from '@/lib/vat';
import { cn } from '@/lib/utils';

type Kind = 'add_back' | 'deduction';
interface Adjustment {
  id: string;
  kind: Kind;
  category: string;
  accountId: string | null;
  accountName: string | null;
  amount: string;
  reason: string;
  createdBy: string | null;
  updatedAt: string;
}
interface Worksheet {
  orgName: string;
  trn: string | null;
  currency: string;
  isFreeZoneClient: boolean;
  fiscalYears: Array<{ id: string; start: string; end: string; label: string; closed: boolean }>;
  fiscalYear: { id: string; start: string; end: string; label: string; closed: boolean };
  dueDate: string;
  profitAndLoss: {
    accounts: Array<{
      accountId: string;
      code: string;
      name: string;
      type: 'income' | 'expense';
      subtype: string;
      amount: string;
      excluded: boolean;
    }>;
    revenue: string;
    expenses: string;
    corporateTaxCharge: string;
    accountingProfit: string;
  };
  worksheet: {
    smallBusinessRelief: boolean;
    qualifyingFreeZone: boolean;
    qualifyingAccountIds: string[];
    qualifyingContactIds: string[];
    lossesBroughtForward: string;
    notes: string | null;
    updatedAt: string | null;
  };
  qualifyingContacts: Array<{ id: string; name: string; revenue: string }>;
  adjustments: Adjustment[];
  suggestions: Array<{
    kind: Kind;
    category: string;
    accountId: string;
    accountName: string;
    amount: string;
    reason: string;
  }>;
  computation: {
    revenue: string;
    accountingProfit: string;
    addBacks: string;
    deductions: string;
    adjustedProfit: string;
    lossRelief: string;
    taxableIncome: string;
    qualifyingRevenue: string;
    nonQualifyingRevenue: string;
    deMinimisLimit: string;
    deMinimisMet: boolean;
    freeZoneApplied: boolean;
    qualifyingIncome: string;
    nonQualifyingIncome: string;
    smallBusinessReliefEligible: boolean;
    smallBusinessReliefApplied: boolean;
    smallBusinessReliefReasons: string[];
    zeroBand: string;
    taxedAtRate: string;
    rate: string;
    tax: string;
    notes: string[];
  };
}

const CATEGORIES: Record<string, string> = {
  entertainment: 'Entertainment (50% non-deductible)',
  fines_penalties: 'Fines and penalties',
  donations: 'Donations',
  related_party: 'Related-party / transfer pricing',
  exempt_dividends: 'Exempt dividends',
  exempt_gains: 'Exempt gains (participation exemption)',
  interest_limitation: 'Interest limitation',
  depreciation: 'Depreciation and fixed assets',
  provisions: 'Provisions',
  other: 'Other',
};
const KINDS: Record<Kind, string> = { add_back: 'Add-back', deduction: 'Deduction' };

export const Route = createFileRoute('/taxes/corporate-tax')({
  component: CorporateTaxPage,
  validateSearch: (s: Record<string, unknown>): { fy?: string } =>
    typeof s['fy'] === 'string' ? { fy: s['fy'] } : {},
});

/** Screen T4 (document 06): corporate tax worksheet per financial year. Computed, not posted. */
function CorporateTaxPage() {
  const orgId = useOrgId();
  const { fy } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const me = useMe();
  const staff = me.data?.isStaff ?? false;
  const q = useQuery({
    queryKey: ['corporate-tax', orgId, fy ?? null],
    queryFn: () => api<Worksheet>(`/orgs/${orgId}/corporate-tax${fy ? `?fiscalYearId=${fy}` : ''}`),
    enabled: Boolean(orgId),
  });
  const [adjust, setAdjust] = useState<{ open: boolean; edit: Partial<Adjustment> | null }>({
    open: false,
    edit: null,
  });
  const [elections, setElections] = useState(false);
  const w = q.data;

  return (
    <>
      <PageHeader
        title="Corporate tax"
        description="Worksheet from the books for the tax adviser. Nothing here is posted or filed."
        actions={
          w ? (
            <>
              <NativeSelect
                aria-label="Financial year"
                className="w-[200px]"
                value={w.fiscalYear.id}
                onChange={(e) => void navigate({ search: { fy: e.target.value } })}
              >
                {w.fiscalYears.map((y) => (
                  <option key={y.id} value={y.id}>
                    {y.label}
                  </option>
                ))}
              </NativeSelect>
              <Button
                variant="ghost"
                size="icon"
                aria-label="Export to Excel (CSV)"
                title="Export to Excel (CSV)"
                onClick={() =>
                  downloadCsv(`Corporate tax ${w.fiscalYear.label}.csv`, worksheetCsv(w))
                }
              >
                <Download className="size-4" />
              </Button>
              <Button variant="ghost" size="icon" aria-label="Print" onClick={() => window.print()}>
                <Printer className="size-4" />
              </Button>
            </>
          ) : null
        }
      />
      {!orgId ? (
        <EmptyState title="Select a client" hint="Use the client switcher in the top bar." />
      ) : q.isPending ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : q.isError || !w ? (
        <EmptyState title="Could not load the worksheet" hint={q.error?.message} />
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-x-8 gap-y-1 rounded-lg border bg-card px-4 py-3 text-sm">
            <span>
              <span className="text-muted-foreground">Tax period </span>
              <span className="font-medium">
                {displayDate(w.fiscalYear.start)} – {displayDate(w.fiscalYear.end)}
              </span>
            </span>
            <span>
              <span className="text-muted-foreground">Return and payment due </span>
              <span className="font-medium">{displayDate(w.dueDate)}</span>
            </span>
            {w.trn ? (
              <span>
                <span className="text-muted-foreground">TRN </span>
                <span className="tnum font-medium">{formatTrn(w.trn)}</span>
              </span>
            ) : null}
            {w.fiscalYear.end >= new Date().toISOString().slice(0, 10) ? (
              <span className="text-[#B45309]">The year is not over: figures to date.</span>
            ) : null}
          </div>

          <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
            <Computation w={w} />
            <Elections w={w} staff={staff} onEdit={() => setElections(true)} />
          </div>

          <section className="mt-4 rounded-lg border bg-card">
            <header className="flex items-center justify-between border-b px-4 py-2.5">
              <div>
                <h2 className="text-base font-semibold">Adjustments</h2>
                <p className="text-xs text-muted-foreground">
                  Add-backs and deductions from accounting profit, each with a reason.
                </p>
              </div>
              {staff ? (
                <Button size="sm" onClick={() => setAdjust({ open: true, edit: null })}>
                  Add adjustment
                </Button>
              ) : null}
            </header>
            {w.adjustments.length === 0 ? (
              <p className="px-4 py-3 text-sm text-muted-foreground">No adjustments yet.</p>
            ) : (
              <AdjustmentTable
                w={w}
                staff={staff}
                onEdit={(a) => setAdjust({ open: true, edit: a })}
              />
            )}
            {staff && w.suggestions.length ? (
              <div className="border-t px-4 py-3">
                <h3 className="mb-2 text-sm font-semibold">Suggested from the accounts</h3>
                <ul className="grid gap-1.5 text-sm">
                  {w.suggestions.map((s) => (
                    <li key={`${s.category}-${s.accountId}`} className="flex items-center gap-3">
                      <span className="flex-1">
                        {KINDS[s.kind]} · {s.accountName}: {s.reason}
                      </span>
                      <span className="tnum">{formatAmount(s.amount)}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAdjust({ open: true, edit: { ...s, id: '' } })}
                      >
                        Add
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <ProfitAndLoss w={w} />
          <AdjustmentDialog
            w={w}
            open={adjust.open}
            edit={adjust.edit}
            onClose={() => setAdjust({ open: false, edit: null })}
          />
          <ElectionsDialog w={w} open={elections} onClose={() => setElections(false)} />
        </>
      )}
    </>
  );
}

function Row({
  label,
  value,
  strong,
  hint,
  indent,
}: {
  label: ReactNode;
  value: string | null;
  strong?: boolean;
  hint?: string | undefined;
  indent?: boolean;
}) {
  return (
    <tr className={cn('border-b border-dashed last:border-0', strong && 'font-semibold')}>
      <td className={cn('py-1.5', indent && 'pl-4')}>
        {label}
        {hint ? <div className="text-xs font-normal text-muted-foreground">{hint}</div> : null}
      </td>
      <td className="tnum w-[170px] py-1.5 text-right align-top">
        {value === null ? '' : formatAmount(value)}
      </td>
    </tr>
  );
}

function Computation({ w }: { w: Worksheet }) {
  const c = w.computation;
  return (
    <section className="rounded-lg border bg-card px-5 py-4" data-testid="ct-computation">
      <h2 className="mb-2 text-base font-semibold">Computation</h2>
      <table className="w-full text-sm">
        <tbody>
          <Row label="Revenue" value={c.revenue} />
          <Row
            label="Accounting profit"
            value={c.accountingProfit}
            strong
            hint="Income less expenses for the year, before the corporate tax charge"
          />
          <Row label="Add: add-backs" value={c.addBacks} indent />
          <Row
            label="Less: deductions"
            value={c.deductions === '0.00' ? '0.00' : `-${c.deductions}`}
            indent
          />
          <Row label="Adjusted profit" value={c.adjustedProfit} strong />
          {c.lossRelief !== '0.00' ? (
            <Row
              label="Less: tax losses brought forward"
              value={`-${c.lossRelief}`}
              indent
              hint="At most 75% of taxable income"
            />
          ) : null}
          <Row label="Taxable income" value={c.taxableIncome} strong />
          {c.smallBusinessReliefApplied ? (
            <Row label="Small business relief: taxable income treated as nil" value="0.00" indent />
          ) : c.freeZoneApplied ? (
            <>
              <Row label="Qualifying income at 0%" value={c.qualifyingIncome} indent />
              <Row
                label={`Non-qualifying income at ${Number(c.rate)}%`}
                value={c.nonQualifyingIncome}
                indent
              />
            </>
          ) : (
            <>
              <Row label={`At 0% (first ${w.currency} 375,000)`} value={c.zeroBand} indent />
              <Row label={`At ${Number(c.rate)}%`} value={c.taxedAtRate} indent />
            </>
          )}
          <tr className="border-t-2 border-foreground font-semibold">
            <td className="py-2">Corporate tax payable</td>
            <td className="tnum py-2 text-right" data-testid="ct-tax">
              {w.currency} {formatAmount(c.tax)}
            </td>
          </tr>
        </tbody>
      </table>
      {c.notes.length ? (
        <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-muted-foreground">
          {c.notes.map((n) => (
            <li key={n}>{n}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function Elections({ w, staff, onEdit }: { w: Worksheet; staff: boolean; onEdit: () => void }) {
  const c = w.computation;
  const ws = w.worksheet;
  return (
    <section className="rounded-lg border bg-card px-5 py-4">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-base font-semibold">Reliefs and free zone</h2>
        {staff ? (
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Edit reliefs and free zone"
            onClick={onEdit}
          >
            <Pencil className="size-4" />
          </Button>
        ) : null}
      </div>
      <dl className="grid gap-3 text-sm">
        <div>
          <dt className="font-medium">Small business relief</dt>
          <dd className="text-muted-foreground">
            {ws.smallBusinessRelief
              ? c.smallBusinessReliefApplied
                ? 'Elected and applied'
                : 'Elected, not available'
              : 'Not elected'}
            {c.smallBusinessReliefEligible
              ? ' · available: revenue is within AED 3,000,000'
              : ` · not available: ${c.smallBusinessReliefReasons.join('; ')}`}
          </dd>
        </div>
        <div>
          <dt className="font-medium">Qualifying free zone person</dt>
          <dd className="text-muted-foreground">
            {ws.qualifyingFreeZone
              ? c.freeZoneApplied
                ? 'Elected: qualifying income at 0%'
                : 'Elected, but the de minimis test is not met'
              : w.isFreeZoneClient
                ? 'Not elected (free zone company)'
                : 'Not elected'}
          </dd>
          {ws.qualifyingFreeZone || w.isFreeZoneClient ? (
            <dd className="mt-1 grid grid-cols-[1fr_auto] gap-x-3 text-xs">
              <span>Qualifying revenue</span>
              <span className="tnum text-right">{formatAmount(c.qualifyingRevenue)}</span>
              <span>Non-qualifying revenue</span>
              <span className="tnum text-right">{formatAmount(c.nonQualifyingRevenue)}</span>
              <span>De minimis limit (lower of 5% and AED 5m)</span>
              <span className="tnum text-right">{formatAmount(c.deMinimisLimit)}</span>
            </dd>
          ) : null}
          {w.qualifyingContacts.length || ws.qualifyingAccountIds.length ? (
            <dd className="mt-1 text-xs text-muted-foreground">
              Tagged:{' '}
              {[
                ...w.profitAndLoss.accounts
                  .filter((a) => ws.qualifyingAccountIds.includes(a.accountId))
                  .map((a) => `${a.name} (account)`),
                ...w.qualifyingContacts.map((x) => x.name),
              ].join(', ')}
            </dd>
          ) : null}
        </div>
        <div>
          <dt className="font-medium">Tax losses brought forward</dt>
          <dd className="tnum text-muted-foreground">
            {w.currency} {formatAmount(ws.lossesBroughtForward)}
          </dd>
        </div>
        {ws.notes ? (
          <div>
            <dt className="font-medium">Notes for the adviser</dt>
            <dd className="whitespace-pre-line text-muted-foreground">{ws.notes}</dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}

function AdjustmentTable({
  w,
  staff,
  onEdit,
}: {
  w: Worksheet;
  staff: boolean;
  onEdit: (a: Adjustment) => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: (id: string) =>
      api(`/orgs/${orgId}/corporate-tax/${w.fiscalYear.id}/adjustments/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['corporate-tax', orgId] });
      toast('Adjustment removed.');
    },
    onError: (e) => toast(e.message, { tone: 'error' }),
  });
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b text-xs uppercase tracking-wide text-muted-foreground">
          <th className="w-[110px] px-4 py-2 text-left font-medium">Type</th>
          <th className="py-2 text-left font-medium">Category</th>
          <th className="py-2 text-left font-medium">Account</th>
          <th className="py-2 text-left font-medium">Reason</th>
          <th className="w-[140px] py-2 text-right font-medium">Amount</th>
          <th className="w-[90px] px-4 py-2" />
        </tr>
      </thead>
      <tbody>
        {w.adjustments.map((a) => (
          <tr key={a.id} className="border-b last:border-0">
            <td className="px-4 py-2">{KINDS[a.kind]}</td>
            <td className="py-2">{CATEGORIES[a.category] ?? a.category}</td>
            <td className="py-2">{a.accountName ?? ''}</td>
            <td className="py-2">
              {a.reason}
              {a.createdBy ? (
                <div className="text-xs text-muted-foreground">{a.createdBy}</div>
              ) : null}
            </td>
            <td className="tnum py-2 text-right">
              {a.kind === 'deduction' ? '-' : ''}
              {formatAmount(a.amount)}
            </td>
            <td className="px-4 py-2 text-right">
              {staff ? (
                <span className="inline-flex">
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Edit adjustment"
                    onClick={() => onEdit(a)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    aria-label="Remove adjustment"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </span>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ProfitAndLoss({ w }: { w: Worksheet }) {
  const [open, setOpen] = useState(false);
  const pl = w.profitAndLoss;
  const income = pl.accounts.filter((a) => a.type === 'income');
  const expenses = pl.accounts.filter((a) => a.type === 'expense');
  return (
    <section className="mt-4 rounded-lg border bg-card">
      <button
        type="button"
        className="flex w-full items-center justify-between px-4 py-2.5 text-left"
        aria-expanded={open}
        onClick={() => setOpen(!open)}
      >
        <span className="text-base font-semibold">Profit and loss for the year</span>
        <span className="text-sm text-primary">{open ? 'Hide' : 'Show'} accounts</span>
      </button>
      {open ? (
        <div className="border-t px-5 py-3">
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <td colSpan={2} className="pb-1 font-semibold">
                  Income
                </td>
              </tr>
              {income.map((a) => (
                <Row key={a.accountId} label={`${a.code} ${a.name}`} value={a.amount} indent />
              ))}
              <Row label="Total income" value={pl.revenue} strong />
              <tr>
                <td colSpan={2} className="pt-3 pb-1 font-semibold">
                  Expenses
                </td>
              </tr>
              {expenses.map((a) => (
                <Row
                  key={a.accountId}
                  label={`${a.code} ${a.name}`}
                  value={a.amount}
                  indent
                  hint={
                    a.excluded ? 'Corporate tax charge: excluded from accounting profit' : undefined
                  }
                />
              ))}
              <Row label="Total expenses (excluding corporate tax)" value={pl.expenses} strong />
              <Row label="Accounting profit" value={pl.accountingProfit} strong />
            </tbody>
          </table>
        </div>
      ) : null}
    </section>
  );
}

function AdjustmentDialog({
  w,
  open,
  edit,
  onClose,
}: {
  w: Worksheet;
  open: boolean;
  edit: Partial<Adjustment> | null;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const accounts = useAccounts();
  const [kind, setKind] = useState<Kind>('add_back');
  const [category, setCategory] = useState('entertainment');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setKind(edit?.kind ?? 'add_back');
      setCategory(edit?.category ?? 'entertainment');
      setAccountId(edit?.accountId ?? null);
      setAmount(edit?.amount ?? '');
      setReason(edit?.reason ?? '');
    }
  }, [open, edit]);
  const isEdit = Boolean(edit?.id);
  const base = `/orgs/${orgId}/corporate-tax/${w.fiscalYear.id}/adjustments`;
  const m = useMutation({
    mutationFn: () =>
      api<Worksheet>(isEdit ? `${base}/${edit!.id}` : base, {
        method: isEdit ? 'PATCH' : 'POST',
        body: JSON.stringify({ kind, category, accountId, amount, reason: reason.trim() }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['corporate-tax', orgId] });
      toast(isEdit ? 'Adjustment saved.' : 'Adjustment added.');
      onClose();
    },
  });
  const errors = serverFieldErrors(m.error);
  const pnl = (accounts.data ?? []).filter((a) => a.class === 'income' || a.class === 'expense');
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit adjustment' : 'Add adjustment'}</DialogTitle>
          <DialogDescription>
            Changes taxable income on this worksheet only; the books are not touched.
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error && !Object.keys(errors).length ? m.error.message : null}</ErrorBanner>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Type">
            {(id) => (
              <NativeSelect id={id} value={kind} onChange={(e) => setKind(e.target.value as Kind)}>
                <option value="add_back">Add-back (increases taxable income)</option>
                <option value="deduction">Deduction (reduces taxable income)</option>
              </NativeSelect>
            )}
          </Field>
          <Field label="Category">
            {(id) => (
              <NativeSelect id={id} value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(CATEGORIES).map(([k, l]) => (
                  <option key={k} value={k}>
                    {l}
                  </option>
                ))}
              </NativeSelect>
            )}
          </Field>
          <Field label="Account (optional)" className="sm:col-span-2" error={errors['accountId']}>
            {() => (
              <Combobox
                ariaLabel="Account"
                items={pnl.map((a) => ({
                  id: a.id,
                  label: a.fullName,
                  keywords: a.code,
                  group: a.accountTypeName,
                }))}
                value={accountId}
                onChange={setAccountId}
              />
            )}
          </Field>
          <Field label={`Amount (${w.currency})`} error={errors['amount']}>
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                value={amount}
                onValueChange={setAmount}
              />
            )}
          </Field>
          <Field label="Reason" className="sm:col-span-2" error={errors['reason']}>
            {(id) => (
              <Textarea
                id={id}
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => m.mutate()}
            disabled={m.isPending || !amount || reason.trim().length < 3}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ElectionsDialog({
  w,
  open,
  onClose,
}: {
  w: Worksheet;
  open: boolean;
  onClose: () => void;
}) {
  const orgId = useOrgId();
  const qc = useQueryClient();
  const customers = useContacts({ kind: 'customer' });
  const [d, setD] = useState(w.worksheet);
  useEffect(() => {
    if (open) setD(w.worksheet);
  }, [open, w.worksheet]);
  const m = useMutation({
    mutationFn: () =>
      api<Worksheet>(`/orgs/${orgId}/corporate-tax/${w.fiscalYear.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          smallBusinessRelief: d.smallBusinessRelief,
          qualifyingFreeZone: d.qualifyingFreeZone,
          qualifyingAccountIds: d.qualifyingAccountIds,
          qualifyingContactIds: d.qualifyingContactIds,
          lossesBroughtForward: d.lossesBroughtForward || '0',
          notes: d.notes ?? '',
        }),
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['corporate-tax', orgId] });
      toast('Worksheet saved.');
      onClose();
    },
  });
  const income = w.profitAndLoss.accounts.filter((a) => a.type === 'income');
  const toggle = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
  const tagged = (customers.data ?? []).filter((c) => d.qualifyingContactIds.includes(c.id));
  return (
    <Dialog open={open} onOpenChange={(o) => (o ? null : (m.reset(), onClose()))}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Reliefs and free zone · {w.fiscalYear.label}</DialogTitle>
          <DialogDescription>
            Small business relief and the qualifying free zone person regime cannot both apply.
          </DialogDescription>
        </DialogHeader>
        <ErrorBanner>{m.error?.message}</ErrorBanner>
        <div className="grid gap-4">
          <Checkbox
            label="Elect small business relief"
            checked={d.smallBusinessRelief}
            onChange={(e) =>
              setD({
                ...d,
                smallBusinessRelief: e.target.checked,
                qualifyingFreeZone: e.target.checked ? false : d.qualifyingFreeZone,
              })
            }
          />
          <Checkbox
            label="Qualifying free zone person"
            checked={d.qualifyingFreeZone}
            onChange={(e) =>
              setD({
                ...d,
                qualifyingFreeZone: e.target.checked,
                smallBusinessRelief: e.target.checked ? false : d.smallBusinessRelief,
              })
            }
          />
          {d.qualifyingFreeZone || w.isFreeZoneClient ? (
            <fieldset className="grid gap-2 rounded-md border p-3">
              <legend className="px-1 text-sm font-medium">Qualifying income</legend>
              <p className="text-xs text-muted-foreground">
                Tag whole income accounts, or customers whose income qualifies (for example free
                zone and overseas clients).
              </p>
              {income.map((a) => (
                <Checkbox
                  key={a.accountId}
                  label={`${a.code} ${a.name}`}
                  checked={d.qualifyingAccountIds.includes(a.accountId)}
                  onChange={() =>
                    setD({
                      ...d,
                      qualifyingAccountIds: toggle(d.qualifyingAccountIds, a.accountId),
                    })
                  }
                />
              ))}
              <Field label="Customers">
                {() => (
                  <Combobox
                    ariaLabel="Add a qualifying customer"
                    placeholder="Add a customer"
                    items={(customers.data ?? [])
                      .filter((c) => !d.qualifyingContactIds.includes(c.id))
                      .map((c) => ({ id: c.id, label: c.displayName }))}
                    value={null}
                    onChange={(id) =>
                      id
                        ? setD({ ...d, qualifyingContactIds: [...d.qualifyingContactIds, id] })
                        : null
                    }
                  />
                )}
              </Field>
              {tagged.length ? (
                <ul className="flex flex-wrap gap-1.5">
                  {tagged.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        className="rounded-full border bg-muted px-2 py-0.5 text-xs hover:bg-accent"
                        aria-label={`Remove ${c.displayName}`}
                        onClick={() =>
                          setD({ ...d, qualifyingContactIds: toggle(d.qualifyingContactIds, c.id) })
                        }
                      >
                        {c.displayName} ×
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </fieldset>
          ) : null}
          <Field label={`Tax losses brought forward (${w.currency})`}>
            {(id) => (
              <AmountInput
                id={id}
                className="h-9 rounded-md border border-input bg-card px-3 text-right text-sm tnum"
                value={d.lossesBroughtForward === '0.00' ? '' : d.lossesBroughtForward}
                onValueChange={(v) => setD({ ...d, lossesBroughtForward: v || '0.00' })}
              />
            )}
          </Field>
          <Field label="Notes for the adviser">
            {(id) => (
              <Textarea
                id={id}
                value={d.notes ?? ''}
                maxLength={4000}
                onChange={(e) => setD({ ...d, notes: e.target.value })}
              />
            )}
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => m.mutate()} disabled={m.isPending}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The worksheet as CSV (opens in Excel) for the tax adviser. */
function worksheetCsv(w: Worksheet): string {
  const c = w.computation;
  const rows: string[][] = [
    [w.orgName],
    [
      `Corporate tax worksheet ${w.fiscalYear.label} (${displayDate(w.fiscalYear.start)} to ${displayDate(w.fiscalYear.end)})`,
    ],
    [`Due ${displayDate(w.dueDate)}`],
    [],
    ['Line', `Amount (${w.currency})`],
    ['Revenue', c.revenue],
    ['Accounting profit', c.accountingProfit],
    ['Add-backs', c.addBacks],
    ['Deductions', c.deductions],
    ['Adjusted profit', c.adjustedProfit],
    ['Tax losses brought forward used', c.lossRelief],
    ['Taxable income', c.taxableIncome],
    ['Small business relief applied', c.smallBusinessReliefApplied ? 'Yes' : 'No'],
    ['Qualifying free zone person applied', c.freeZoneApplied ? 'Yes' : 'No'],
    ['Qualifying revenue', c.qualifyingRevenue],
    ['Non-qualifying revenue', c.nonQualifyingRevenue],
    ['At 0%', c.freeZoneApplied ? c.qualifyingIncome : c.zeroBand],
    [`At ${Number(c.rate)}%`, c.taxedAtRate],
    ['Corporate tax payable', c.tax],
    [],
    ['Adjustments'],
    ['Type', 'Category', 'Account', 'Amount', 'Reason'],
    ...w.adjustments.map((a) => [
      KINDS[a.kind],
      CATEGORIES[a.category] ?? a.category,
      a.accountName ?? '',
      a.amount,
      a.reason,
    ]),
    [],
    ['Notes'],
    ...c.notes.map((n) => [n]),
    ...(w.worksheet.notes ? [[w.worksheet.notes]] : []),
  ];
  return Papa.unparse(rows);
}
